import { useState, useEffect, useRef, useCallback } from "react";
import { classNameHelper } from "../../other/utils/utils.jsx";

export const SwivuMenu = ({
  options = [],
  extraOptions = [],
  isOpen = false,
  onItemClick,
  onClose,
  noResultsLabel = "No results found",
  className = "",
  maxHeight = "max-h-60"
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const menuRef = useRef(null);

  const allOptions = [
    ...options.filter(option => !option.hidden),
    ...extraOptions.filter(option => !option.hidden)
  ];

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => {
          const maxIndex = allOptions.length - 1;
          return prev < maxIndex ? prev + 1 : 0;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => {
          const maxIndex = allOptions.length - 1;
          return prev > 0 ? prev - 1 : maxIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && allOptions[highlightedIndex]) {
          const option = allOptions[highlightedIndex];
          if (!option.disabled) {
            onItemClick?.(option);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        onClose?.();
        break;
      case "Tab":
        onClose?.();
        break;
    }
  }, [isOpen, highlightedIndex, allOptions, onItemClick, onClose]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isOpen, handleKeyDown, onClose]);

  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  const handleOptionClick = useCallback((option, event) => {
    if (!option.disabled) {
      event?.stopPropagation();
      onItemClick?.(option);
    }
  }, [onItemClick]);

  const handleMouseEnter = useCallback((index) => {
    setHighlightedIndex(index);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} />

      <div
        ref={menuRef}
        className={classNameHelper(
          "absolute top-full left-0 right-0 z-20 mt-2 bg-card rounded-swivu-base shadow-xl overflow-y-auto border border-border border-solid",
          maxHeight,
          className
        )}
      >
        {allOptions.length > 0 ? (
          allOptions.map((option, index) => (
            <SwivuMenuItem
              key={`${option.value || option.label}-${index}`}
              option={option}
              index={index}
              totalOptions={allOptions.length}
              isHighlighted={highlightedIndex === index}
              onClick={(e) => handleOptionClick(option, e)}
              onMouseEnter={() => handleMouseEnter(index)}
            />
          ))
        ) : (
          <div className="px-4 py-3 text-base text-muted-foreground h-full leading-none">
            {noResultsLabel}
          </div>
        )}
      </div>
    </>
  );
};

export const SwivuMenuItem = ({
  onClick,
  option,
  index,
  totalOptions,
  isHighlighted,
  onMouseEnter
}) => {
  const isDisabled = option.disabled;

  const handleClick = (e) => {
    e.stopPropagation();
    onClick(e);
  };

  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      className={classNameHelper(
        "w-full pl-4 pr-8 py-3 text-left text-base transition-colors font-swivu-modern bg-transparent border-0 first:rounded-t-swivu-base min-w-max flex items-center gap-2 h-swivu-input",
        option.className,
        {
          'last:rounded-b-swivu-base': index === totalOptions - 1,
          '!bg-secondary !text-secondary-foreground': isHighlighted && !isDisabled,
          'text-secondary-foreground cursor-pointer hover:!bg-secondary hover:!text-secondary-foreground':
            !isHighlighted && !isDisabled,
          'opacity-50 text-muted-foreground':
            isDisabled
        }
      )}
    >
      {option.icon && (
        <i
          className={classNameHelper(
            "h-4 w-4",
            isDisabled
              ? "text-muted-foreground opacity-50"
              : "text-muted-foreground",
            option.icon
          )}
        />
      )}
      {option.label}
    </button>
  );
};
